import { Link } from 'react-router-dom';

import { AsyncBoundary, Icon, useToast } from '@kollez/design-system';
import { useMeDashboard, type MeDashboardResponse } from '@/lib/queries';
import { buildIcsFile, downloadIcs } from '@/lib/ics';
import { formatLPA, formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /home — Student dashboard.
 *
 * Backed by GET /api/v1/students/me/dashboard (Pass 14). One composite call replaces
 * the old 3-request fan-out (applications + offers + calendar). The server returns
 * everything the page needs — KPIs + upcoming interviews + pending offers + recent
 * apps + eligible-drive preview + lastActivity — so the page renders in one round
 * trip.
 *
 * Sections (top → bottom):
 *   - Header greeting: chooses the most urgent copy from the composite's counts
 *   - Next step: inkwell card for the top pending offer (if any)
 *   - Nexie ai-suggest strips: readiness + mock warm-up
 *   - Coming up: upcomingInterviews[]
 *   - My applications: recentApplications[] with 5-dot stage indicator
 *   - Eligible drives: eligibleDrivesPreview[] (Pass 14 addition — new drives you can apply to)
 *   - Profile ring
 */
export function StudentHomePage() {
  const dash = useMeDashboard();
  const toast = useToast();

  return (
    <AsyncBoundary
      kind="detail"
      loading={dash.isLoading}
      error={dash.isError ? (dash.error instanceof Error ? dash.error.message : true) : undefined}
      onRetry={() => void dash.refetch()}
    >
      {dash.data ? (
        <HomeInner
          data={dash.data}
          onCalendarAdd={(iv) => {
            const dayObj = new Date(iv.scheduledAt);
            downloadIcs(
              `interview-${dayObj.toISOString().slice(0, 10)}.ics`,
              buildIcsFile({
                scheduledAt: iv.scheduledAt,
                roundType: iv.roundType,
                room: iv.room,
                title: iv.drive?.driveName ? `Interview · ${iv.drive.driveName}` : undefined,
              }),
            );
            toast.show({ message: 'Downloaded .ics — open it to add the event to your calendar.' });
          }}
        />
      ) : null}
    </AsyncBoundary>
  );
}

function HomeInner({ data, onCalendarAdd }: { data: MeDashboardResponse; onCalendarAdd: (iv: MeDashboardResponse['upcomingInterviews'][number]) => void }) {
  const { kpis, upcomingInterviews, pendingOffers, recentApplications, eligibleDrivesPreview } = data;
  const openOffer = pendingOffers[0];   // server sorts by deadline asc, so [0] is the most-urgent
  const applicationsCount = Object.entries(kpis.applicationsByStatus)
    .filter(([status]) => !/rejected|withdrawn/i.test(status))
    .reduce((n, [, count]) => n + count, 0);

  const greetingSubhead = openOffer && upcomingInterviews.length > 0
    ? 'You have an offer to reply to and an interview coming up.'
    : openOffer
    ? 'You have an offer to reply to.'
    : upcomingInterviews.length > 0
    ? "You've got upcoming interviews and tests."
    : kpis.pendingOfferCount === 0 && applicationsCount === 0
    ? 'No urgent items — a good time to sharpen your resume or take a mock.'
    : "Nothing urgent — check what's coming up below.";

  return (
    <div className="stack">
      <style>{`
        .dots { display: flex; gap: 3px; }
        .dots i { width: 18px; height: 4px; border-radius: 2px; background: rgb(var(--bg-tertiary)); }
        .dots i.on { background: rgb(var(--ink-accent)); }
        .dots i.bad { background: rgb(var(--ink-danger)); }
        .dots i.win { background: rgb(var(--ink-success)); }
        .app-row { display: grid; gap: 12px; align-items: center; padding: 12px 16px; border-bottom: 1px solid rgb(var(--line-tertiary)); color: inherit; text-decoration: none; }
        .app-row:last-child { border-bottom: 0; }
        .app-row:hover { background: rgb(var(--bg-secondary)); }
        .date-tile { width: 44px; text-align: center; border: 1px solid rgb(var(--line-secondary)); border-radius: 8px; overflow: hidden; flex-shrink: 0; }
        .date-tile b { display: block; font-family: "Newsreader", Georgia, serif; font-size: 1.25rem; font-weight: 500; line-height: 1.3; }
        .date-tile span { display: block; font-size: .625rem; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; background: rgb(var(--margin)); color: #fff; padding: 1px 0; }
        .badge-count { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px; background: rgb(var(--bg-tertiary)); font-size: .75rem; font-weight: 600; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">{new Date().toLocaleString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}</p>
          <h1 className="h-page">Hi there</h1>
          <p className="subhead">{greetingSubhead}</p>
        </div>
      </div>

      {/* Next step — inkwell card for the most urgent open offer. */}
      {openOffer && (
        <section className="card inkwell" style={{ position: 'relative', overflow: 'hidden', padding: '20px 20px 20px 28px' }}>
          <span
            aria-hidden="true"
            style={{ position: 'absolute', left: 14, top: 0, bottom: 0, width: 1, background: 'rgb(var(--margin) / .7)' }}
          />
          <p className="text-[13px] opacity-80 m-0">Your next step</p>
          <h2 className="h-serif text-[1.625rem] leading-tight mt-1 mb-1">
            {openOffer.partner?.name ?? 'Company'} offered you {openOffer.role ?? openOffer.designation ?? 'a role'}
          </h2>
          <p className="opacity-85 m-0 text-sm">
            {typeof openOffer.ctc?.amountInr === 'number' ? formatLPA(openOffer.ctc.amountInr) : 'CTC on the letter'}
            {openOffer.deadlineAt ? ` · reply by ${formatIndianDate(openOffer.deadlineAt)}` : ''}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link className="btn btn-lg" style={{ background: '#fff', color: 'rgb(21 34 82)' }} to={`/offers/${openOffer._id}`}>
              See offer and reply
            </Link>
          </div>
        </section>
      )}

      {/* Nexie ai-suggest strips */}
      <Link to="/profile-360" className="ai-suggest">
        <Icon name="radar" size={16} />
        <div className="flex-1"><b>Readiness score.</b> Your two biggest wins are within reach on your 360.</div>
      </Link>
      <Link to="/mock" className="ai-suggest">
        <Icon name="clipboard-check" size={16} />
        <div className="flex-1"><b>Warm up with Nexie.</b> 5 questions per session, graded on the spot, 40 min or less.</div>
      </Link>

      {/* Coming up — upcoming interviews (server returns next 5, we show up to 4) */}
      {upcomingInterviews.length > 0 && (
        <section className="card">
          <div className="card-head">
            <h2 className="h-section">Coming up</h2>
            {kpis.upcomingInterviewCount > 4 && (
              <Link to="/interviews" className="helper">See all {kpis.upcomingInterviewCount}</Link>
            )}
          </div>
          <div className="grid">
            {upcomingInterviews.slice(0, 4).map((iv) => {
              const d = new Date(iv.scheduledAt);
              const title = iv.drive?.driveName
                ? `Interview · ${iv.drive.driveName}`
                : `Interview · Round ${iv.roundNumber ?? 1}`;
              return (
                <Link
                  key={iv._id}
                  to={`/interviews/${iv._id}`}
                  className="app-row"
                  style={{ gridTemplateColumns: '44px minmax(0,1fr) auto' }}
                >
                  <div className="date-tile">
                    <span>{d.toLocaleString('en-IN', { month: 'short' })}</span>
                    <b>{d.getDate()}</b>
                  </div>
                  <div className="min-w-0">
                    <div className="cell-title">{title}</div>
                    <div className="cell-sub">
                      {formatTime(iv.scheduledAt)}
                      {iv.room ? ` · ${iv.room}` : ''}
                      {iv.roundType ? ` · ${iv.roundType.replace(/_/g, ' ')}` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-icon"
                    aria-label="Add to calendar"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCalendarAdd(iv); }}
                  >
                    <Icon name="calendar-plus" size={14} />
                  </button>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* My applications — 5-dot stage indicator */}
      {recentApplications.length > 0 && (
        <section className="card">
          <div className="card-head">
            <h2 className="h-section">My applications</h2>
            <span className="helper">{applicationsCount} active</span>
          </div>
          <div>
            {recentApplications.map((a) => {
              const stage = stageIndex(a.status);
              const state = /accept/i.test(a.status) ? 'win' : /reject/i.test(a.status) ? 'bad' : 'on';
              const driveName = a.drive?.driveName ?? 'Drive';
              return (
                <Link
                  key={a._id}
                  to={`/applications/${a._id}`}
                  className="app-row"
                  style={{ gridTemplateColumns: '36px minmax(0,1fr) auto', textDecoration: 'none' }}
                >
                  <span className="logo-tile">{initials(driveName)}</span>
                  <div className="min-w-0">
                    <div className="cell-title truncate">{driveName}</div>
                    <div className="cell-sub truncate">
                      {humanStatus(a.status)}
                      {typeof a.matchScore?.overall === 'number' ? ` · match ${a.matchScore.overall}` : ''}
                    </div>
                  </div>
                  <div className="dots" role="img" aria-label={`Stage ${stage + 1} of 5`}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <i
                        key={i}
                        className={
                          i <= stage
                            ? state === 'bad' && i === stage ? 'bad' : state === 'win' ? 'win' : 'on'
                            : ''
                        }
                      />
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Eligible drives you haven't applied to yet — Pass 14 addition */}
      {eligibleDrivesPreview.length > 0 && (
        <section className="card">
          <div className="card-head">
            <h2 className="h-section">Drives you can apply to</h2>
            <Link to="/explore" className="helper">Explore all</Link>
          </div>
          <div>
            {eligibleDrivesPreview.map((d) => {
              const dt = d.driveDate ? new Date(d.driveDate) : null;
              const collegeLabel = typeof d.college === 'string'
                ? d.college
                : d.college?.name ?? '';
              return (
                <Link
                  key={d._id}
                  to={`/drives/${d._id}`}
                  className="app-row"
                  style={{ gridTemplateColumns: '44px minmax(0,1fr) auto', textDecoration: 'none' }}
                >
                  {dt ? (
                    <div className="date-tile">
                      <span>{dt.toLocaleString('en-IN', { month: 'short' })}</span>
                      <b>{dt.getDate()}</b>
                    </div>
                  ) : (
                    <span className="logo-tile">{initials(d.driveName ?? 'D')}</span>
                  )}
                  <div className="min-w-0">
                    <div className="cell-title truncate">{d.driveName ?? 'Drive'}</div>
                    <div className="cell-sub truncate">
                      {d.partner?.name ?? ''}
                      {d.partner?.name && collegeLabel ? ' · ' : ''}
                      {collegeLabel}
                    </div>
                  </div>
                  <Icon name="chevron-right" size={16} />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Profile ring — static readiness prompt; live number comes from /profile-360 */}
      <section className="card card-pad grid gap-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {kpis.activeBadges > 0 && (
              <span className="badge-count" title="Active badges">
                <Icon name="award" size={12} />
                {kpis.activeBadges}
              </span>
            )}
            {kpis.unreadNotifications > 0 && (
              <Link to="/notifications" className="badge-count" title="Unread notifications">
                <Icon name="bell" size={12} />
                {kpis.unreadNotifications}
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="min-w-0">
            <h2 className="h-section">Keep your profile strong</h2>
            <p className="helper">A GitHub-backed project matches more drives. Take 5 minutes.</p>
          </div>
        </div>
        <Link className="btn btn-outline justify-self-start" to="/projects">Add a project</Link>
      </section>
    </div>
  );
}

function stageIndex(status: string): number {
  const s = status.toLowerCase();
  if (/accept/.test(s)) return 4;
  if (/offer/.test(s)) return 4;
  if (/interview/.test(s)) return 3;
  if (/test|assess/.test(s)) return 2;
  if (/short/.test(s)) return 1;
  return 0;
}

function humanStatus(status: string): string {
  return status.replace(/_/g, ' ');
}

function initials(name: string): string {
  return name.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 3).toUpperCase();
}
